import { SetStateAction, useRef, useState } from 'react';
import { ImagesType, RebateOrOfferType } from '../..';
import FeaturedCard from '../featuredCard';
import RebateList from './rebateList';

const Cards = ({
  setSelectedOffer,
  offers,
  title,
  images,
  buttonsLabel,
  type,
  scrollRef,
}: {
  setSelectedOffer: React.Dispatch<SetStateAction<RebateOrOfferType>>;
  offers: RebateOrOfferType[];
  title: string;
  images?: ImagesType;
  buttonsLabel: string;
  type?: 'offer' | 'rebate';
  scrollRef?: any;
}) => {
  const [intersectingCardIndex, setIntersectingCardIndex] = useState(0);
  const autoScrollRef = useRef<HTMLUListElement | null>(null);

  const executeScroll = (index: number, ref: any) => {
    const refChildren = ref.current.children;
    const cards = [];
    // filtering out role="presentation" <li>s
    for (let i = 0; i < refChildren.length; i++) {
      const child = refChildren[i];
      if (child.getAttribute('role') !== 'presentation') {
        cards.push(child);
      }
    }
    cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // if two cards, show them both side by side
  const showFirstFeaturedCard = offers && offers.length < 3;

  return (
    <div className="py-16">
      <h2 className="text-center text-gray-darker mt-36 pt-28 mb-24 text-2xl" ref={scrollRef}>
        {title}
      </h2>
      <div className="container-4xl mb-64">
        <ul
          ref={autoScrollRef}
          className="snap md:snap-none scrollbar-none flex md:flex-wrap md:justify-center px-16 py-24 md:p-0 -mx-16 -my-24 md:-m-8 lg:-m-12 overflow-x-auto md:overflow-x-visible"
        >
          {/* Empty presentational list item to force the first card to center during scroll-snap on small devices */}
          <li className="flex-shrink-0 w-11/12 sm:w-7/12 md:hidden" role="presentation"></li>
          {showFirstFeaturedCard && (
            <FeaturedCard
              offer={offers[0]}
              setSelectedOffer={setSelectedOffer}
              images={images}
              shouldZoom={intersectingCardIndex === 0}
              index={0}
              setIntersectingCardIndex={setIntersectingCardIndex}
              btn={buttonsLabel}
            />
          )}
          {offers && offers.length === 2 && (
            <FeaturedCard
              offer={offers[1]}
              setSelectedOffer={setSelectedOffer}
              images={images}
              shouldZoom={intersectingCardIndex === 1}
              index={1}
              setIntersectingCardIndex={setIntersectingCardIndex}
              btn={buttonsLabel}
            />
          )}
          {offers && offers.length > 2 && (
            <RebateList
              images={images}
              setSelectedOffer={setSelectedOffer}
              rebates={offers}
              btn={buttonsLabel}
              intersectingCardIndex={intersectingCardIndex}
              indexAdjustment={0}
              setIntersectingCardIndex={setIntersectingCardIndex}
            />
          )}

          {/* Empty presentational list item to force the last card to center during scroll-snap on small devices */}
          <li className="flex-shrink-0 w-11/12 sm:w-7/12 md:hidden" role="presentation"></li>
        </ul>
        {offers && offers.length > 1 && (
          <ol
            className="relative flex justify-center mt-24 space-x-16 md:hidden"
            aria-hidden="true"
          >
            {offers.map((offer, index) => (
              <li key={`offer-${index + 1}`}>
                <button
                  className={`block w-14 h-14 rounded-full border-2 border-blue ${
                    index === intersectingCardIndex ? 'bg-blue' : 'bg-transparent'
                  }`}
                  type="button"
                  onClick={() => {
                    if (autoScrollRef) {
                      executeScroll(index, autoScrollRef);
                    }
                  }}
                >
                  <span className="sr-only">{`Card ${index + 1}`}</span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
      {type === 'rebate' && (
        <div className="text-sm text-gray-darker text-center max-w-xl mx-auto my-32">
          While non-Duke Energy customers are eligible to use the referral service and receive
          special contractor discounts and financing, only Duke Energy customers are eligible to
          receive Duke Energy rebates. The referral service is not available to customers residing
          in Florida.
        </div>
      )}
    </div>
  );
};

export default Cards;
